import React, { useState, useEffect } from 'react';
import { downloadVariantPdf } from '../../utils/variantPdf';
import { useNavigate, useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import Loader from '../../components/Common/Loader';
import LockedModal from '../../components/Student/LockedModal';
import { getVariants } from '../../services/studentService';

/* ── Дизайн из Claude Design (Variant Card Wide.dc.html): широкая карточка в строку,
   диаграмма баллов справа за фикс-разделителем. На токенах темы + liquid glass. ── */

const IcClip = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
    <rect x="9" y="3" width="6" height="4" rx="2"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="12" y2="16"/>
  </svg>
);
// Дата варианта в компактном виде «11.12» — она идёт строкой у мета-данных,
// а в бейдже слева теперь иконка раздела «Варианты».
const dayMonth = (d) => {
  const x = d ? new Date(d) : null;
  if (!x || isNaN(x)) return null;
  return `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}`;
};
const IcDownload = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>);
const IcArrowR = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>);
const IcLockSm = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10.5" width="16" height="10.5" rx="2.4"/><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"/></svg>);
const IcCheck = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>);

const plural = (n, forms) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
};

const TestsPage = () => {
  const [lockInfo, setLockInfo] = useState(null);   // вариант вне пробного доступа
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { selectedCourseId } = useOutletContext() || {};

  useEffect(() => {
    getVariants(selectedCourseId).then(res => setVariants(res.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [selectedCourseId]);

  const open = (id) => navigate(`/variant/${id}`, { state: { from: '/tests' } });
  // Бланк собирается в фоне и сразу падает файлом — без лишних окон
  const downloadPdf = (v) => downloadVariantPdf(v.id, v.title);

  // Порядок — тот же, что задал админ (бэкенд отдаёт по sortOrder): сданные
  // больше не всплывают наверх, иначе список не совпадал с редактором курса.
  const sorted = variants;

  if (loading) return <div className="s-page"><Loader /></div>;

  return (
    <div className="s-page">
      <style>{`
        .tvar-card { display:flex; flex-wrap:wrap; align-items:center; gap:18px; border-radius:18px; padding:18px 20px;
          background: var(--lg-sheen), var(--card); border:1px solid var(--lg-edge);
          box-shadow: inset 0 1px 0 var(--lg-hi), inset 0 0 0 1px var(--lg-ring), var(--lg-shadow);
          transition: background .16s; }
        .tvar-card:hover { background: linear-gradient(rgba(124,77,255,0.06), rgba(124,77,255,0.06)), var(--lg-sheen), var(--card); }
        /* бейдж с датой крупнее, чем в общих списках — карточка варианта выше */
        .tvar-card .test-date { width:54px; height:56px; border-radius:14px; }
        .tvar-card .test-date b { font-size:21px; }
        .tvar-card .test-date i { font-size:10px; margin-top:3px; }
        .tvar-card .test-date svg { width:24px; height:24px; }
        body:not(.dark) .tvar-card:hover { background: linear-gradient(rgba(255,255,255,0.6), rgba(255,255,255,0.6)), var(--lg-sheen), var(--card); }
        /* Ширина фиксирована с запасом под «Баллов ещё нет» — иначе у несданного
           пробника блок распирало по контенту и разделитель уезжал влево.
           Колонки прижаты по низу (подписи в одну линию), а меньшую цифру
           поднимаем на 2px — при line-height 0.92 её базовая линия иначе ниже. */
        .tvar-score { flex:0 0 290px; min-width:0; display:flex; align-items:flex-end; gap:14px;
          padding-left:20px; border-left:1px solid var(--border); }
        .tvar-actions { flex:0 0 200px; display:flex; align-items:center; justify-content:flex-end; gap:9px; }
        .tvar-pdf { width:38px; height:38px; flex-shrink:0; border-radius:11px; border:1px solid rgba(220,76,76,0.35);
          background:rgba(220,76,76,0.08); color:#d0433f; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:.15s; }
        .tvar-pdf:hover { border-color:#d0433f; color:#e15b57; background:rgba(220,76,76,0.16); }
        .tvar-go { height:38px; width:150px; padding:0 12px; border-radius:11px; border:none; background:var(--primary); color:#fff; font-family:inherit;
          font-size:12.5px; font-weight:600; display:inline-flex; align-items:center; justify-content:center; gap:7px; cursor:pointer; white-space:nowrap; transition:background .15s; }
        .tvar-go:hover { background:var(--primary-dark); }
        /* Баллы — Bebas Neue: итоговый крупно, первичный поменьше, знаменатель тише */
        .tvar-num { display:inline-flex; align-items:baseline; font-family:'Bebas Neue','Oswald',sans-serif;
          font-size:52px; font-weight:400; line-height:0.92; letter-spacing:1px;
          /* вторичный балл — тем же цветом, что и первичный, а не фиолетовым */
          color:var(--text-1); font-variant-numeric:tabular-nums; }
        /* глобальный * { font-family } перебивал наследование — задаём явно */
        .tvar-num .max { font-family:'Bebas Neue','Oswald',sans-serif; font-size:0.45em;
          letter-spacing:0.5px; color:var(--text-3); margin-left:2px; }
        .tvar-num.sm { font-size:38px; color:var(--text-1); margin-bottom:2px; }
        .tvar-num.empty { color:var(--text-3); }
        /* «из 100» оформлен так же, как «Первичный балл» → подписи в одну линию */
        .tvar-of, .tvar-cap { margin-top:2px; font-size:10.5px; font-weight:800; line-height:16px;
          letter-spacing:0.5px; text-transform:uppercase; color:var(--text-3); white-space:nowrap; }
        body.dark .tvar-num { color:#fff !important; }
        /* Узкий экран: баллы и действия переносятся на свои строки. Действия
           прижимаем вправо, баллы отделяем линией сверху, а не слева. */
        @media (max-width:820px){
          .tvar-card { gap:14px; padding:16px; }
          .tvar-score { flex:1 1 100%; padding:14px 0 0; border-left:none; border-top:1px solid var(--border);
            justify-content:flex-start; gap:26px; }
          .tvar-actions { flex:1 1 100%; justify-content:flex-end; }
          .tvar-go { width:auto; min-width:170px; }
        }
        @media (max-width:420px){
          .tvar-go { min-width:150px; }
        }
      `}</style>

      <div className="s-page-header">
        <div>
          <h1 className="s-page-title">Варианты</h1>
          <p className="s-page-sub">Пробные экзамены с автопроверкой и переводом баллов</p>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="pg-empty">Вариантов пока нет</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sorted.map(v => {
            const isOge = v.examType === 'ОГЭ';
            const secondVal = isOge ? v.grade : v.secondary;
            const secondMax = isOge ? 5 : 100;

            return (
              <div key={v.id} className={`tvar-card${v.locked ? ' locked' : ''}`}
                onClick={() => (v.locked ? setLockInfo({ kind: 'variant', title: v.title }) : open(v.id))}
                style={{ cursor: v.locked ? 'default' : 'pointer' }}>

                <span className="test-date"><IcClip /></span>

                {/* Левый блок: название + статус + мета + прогресс */}
                <div style={{ flex: '1 1 240px', minWidth: 230, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.3px' }}>{v.title}</span>
                    {v.locked ? (
                      <span className="lock-chip" title="Откроется с полным курсом"><IcLockSm /></span>
                    ) : v.completed ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: 'rgba(34,197,94,0.14)', color: '#16a34a', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase', flexShrink: 0 }}><IcCheck />Сдан</span>
                    ) : (
                      <span className="tvar-avail" style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 20, background: 'var(--primary-light)', color: 'var(--primary-dark)', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase', flexShrink: 0 }}>Доступен</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{v.taskCount} {plural(v.taskCount, ['задание', 'задания', 'заданий'])}{dayMonth(v.date) ? ` · ${dayMonth(v.date)}` : ''}</div>
                  {/* Куратор выдал ещё попытку: балл остаётся, засчитается лучший */}
                  {v.completed && (v.retryActive || v.extraAttempts > 0) && (
                    <div className="s-attempt">{v.retryActive ? 'Идёт повторная попытка' : 'Доступна ещё одна попытка'}</div>
                  )}
                </div>

                {/* Блок баллов (фикс-ширина → разделитель на одном месте) */}
                <div className="tvar-score">
                  {v.completed ? (
                    <>
                      <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 96 }}>
                        <span className="tvar-num">{secondVal != null ? secondVal : '—'}</span>
                        <span className="tvar-of">из {secondMax}</span>
                      </div>
                      <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span className="tvar-num sm">{v.primary}<span className="max">/{v.maxPrimary}</span></span>
                        <span className="tvar-cap">Первичный балл</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 96 }}>
                        <span className="tvar-num empty">—</span>
                        <span className="tvar-of">из {secondMax}</span>
                      </div>
                      <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3 }}>
                        <div style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 600, whiteSpace: 'nowrap' }}>Баллов ещё нет</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>появятся после отправки</div>
                      </div>
                    </>
                  )}
                </div>

                {/* Действия (фикс-ширина) */}
                <div className="tvar-actions">
                  {!v.locked && (
                    <>
                      <button className="tvar-pdf" title="Скачать PDF" aria-label="Скачать PDF" onClick={(e) => { e.stopPropagation(); downloadPdf(v); }}><IcDownload /></button>
                      <button className="tvar-go" onClick={(e) => { e.stopPropagation(); open(v.id); }}>
                        {v.completed ? (v.retryActive ? 'Продолжить' : 'Разбор ошибок') : (v.answered ? 'Продолжить' : 'Выполнить')}<IcArrowR />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <LockedModal open={!!lockInfo} onClose={() => setLockInfo(null)}
        kind={lockInfo?.kind} title={lockInfo?.title} />
    </div>
  );
};

export default TestsPage;
